import { Box, IconButton } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import { Link } from "@tanstack/react-router"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import type { ProductCostBreakdownRow } from "../types"

import DateChip from "@/components/DateChip"
import { PurchaseOrderNumberCell } from "@/components/table"
import { CostBreakdownQuantityCell } from "@/screens/Procurement/CostBreakdown/components/CostBreakdownTable"
import { formatCostBreakdownCurrency } from "@/screens/Procurement/CostBreakdown/utils/formatCostBreakdownCurrency"
import { ProductVersionChip } from "@/screens/Products/components/ProductVersionChip"
import { formatWarehouseLabel } from "@/utils/warehouse"

export const useProductCostBreakdownColumns = (): ColumnDef<ProductCostBreakdownRow>[] => {
  return useMemo<ColumnDef<ProductCostBreakdownRow>[]>(() => {
    return [
      {
        accessorKey: "grNumber",
        header: "GR #",
        enableSorting: false,
        cell: ({ row }) => {
          const { agrNumber } = row.original.goodsReceipt ?? {}

          return agrNumber ? (
            <Link
              to='/app/procurement/cost-breakdown'
              search={{ query: agrNumber, isProductCostCompleted: [true, false] }}
              className='font-bold text-primary underline'
            >
              {agrNumber}
            </Link>
          ) : null
        },
      },
      {
        accessorKey: "poNumber",
        header: "PO #",
        enableSorting: false,
        cell: ({ row }) => {
          const order = row.original.purchaseOrderLine?.orderDetail?.order
          return order ? <PurchaseOrderNumberCell order={order} /> : null
        },
      },
      {
        accessorKey: "productVersion",
        header: "Product Version",
        enableSorting: false,
        cell: ({ row }) => {
          const product = row.original.purchaseOrderLine?.product ?? row.original.costBreakdown.product
          const currentVersion = product?.versions?.find((version) => version.id === product.versionId)

          if (!currentVersion) {
            return null
          }

          return <ProductVersionChip version={currentVersion} />
        },
      },
      {
        accessorKey: "warehouse",
        header: "Warehouse",
        enableSorting: true,
        cell: ({ row }) => {
          const { warehouse } = row.original.costBreakdown
          return warehouse ? formatWarehouseLabel(warehouse) : null
        },
      },
      {
        accessorKey: "quantity",
        header: "Qty Received",
        enableSorting: false,
        cell: ({ row }) => {
          const { purchaseOrderLine } = row.original
          return purchaseOrderLine ? <CostBreakdownQuantityCell purchaseOrderLine={purchaseOrderLine} /> : null
        },
      },
      {
        accessorKey: "laidDownCost",
        header: "Total Laid-Down Cost per UoM",
        enableSorting: false,
        cell: ({ row }) => formatCostBreakdownCurrency(row.original.costBreakdown.laidDownCost),
      },
      {
        accessorKey: "lastReceiptDate",
        header: "Received Date",
        enableSorting: true,
        cell: ({ row }) => {
          const { lastReceiptDate } = row.original.costBreakdown
          return lastReceiptDate ? <DateChip date={lastReceiptDate} className='bg-primary-200' /> : null
        },
      },
      {
        accessorKey: "laidDownDate",
        header: "Laid Down Date",
        enableSorting: true,
        cell: ({ row }) => {
          const { laidDownDate } = row.original.costBreakdown
          return laidDownDate ? <DateChip date={laidDownDate} className='bg-primary-200' /> : null
        },
      },
      {
        id: "expander",
        header: "",
        enableSorting: false,
        size: 70,
        cell: ({ row }) => (
          <Box className='flex items-center justify-end' onClick={(e) => e.stopPropagation()}>
            <IconButton size='small' onClick={row.getToggleExpandedHandler()}>
              {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          </Box>
        ),
      },
    ]
  }, [])
}
